import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Edit,
  Save,
  Car,
  Calendar,
  Gauge,
  Fuel,
  Cog,
  Loader2,
  Copy,
  Check,
  Key,
  User,
  Clock,
  Camera,
  Phone,
  Mail,
  FileText,
  Megaphone,
} from "lucide-react";
import {
  DepotVenteListingForm,
  EMPTY_LISTING_FORM,
  listingToForm,
  buildTitle,
  buildKmLabel,
  buildImageUrl,
} from "../components/depotvente/DepotVenteListingForm";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  disponible: { label: "Disponible", cls: "bg-green-500/20 text-green-300 border-green-500/30" },
  reserve: { label: "Réservé", cls: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30" },
  vendu: { label: "Vendu", cls: "bg-red-500/20 text-red-300 border-red-500/30" },
};

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.disponible;
  return (
    <span className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded border ${meta.cls}`}>
      {meta.label}
    </span>
  );
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
};

export default function DepotVenteTab() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [copied, setCopied] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_LISTING_FORM);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  // Page copy (section titles + CTA) — public-facing text on /achat-vente-voiture-clermont-ferrand
  const [pageMeta, setPageMeta] = useState({
    section_title: "",
    section_subtitle: "",
    empty_state: "",
    cta_label: "",
    cta_phone: "",
  });
  const [savingMeta, setSavingMeta] = useState(false);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/depot-vente/listings`);
      setListings(res.data.listings || []);
    } catch {
      toast.error("Erreur au chargement des annonces");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPassword = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/depot-vente/password`);
      setPassword(res.data.password || "");
    } catch {
      // silent
    }
  }, []);

  const fetchPageMeta = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/depot-vente/page-meta`);
      setPageMeta({
        section_title: res.data.section_title || "",
        section_subtitle: res.data.section_subtitle || "",
        empty_state: res.data.empty_state || "",
        cta_label: res.data.cta_label || "",
        cta_phone: res.data.cta_phone || "",
      });
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchListings();
    fetchPassword();
    fetchPageMeta();
  }, [fetchListings, fetchPassword, fetchPageMeta]);

  const savePageMeta = async () => {
    setSavingMeta(true);
    try {
      await axios.put(`${API}/api/admin/depot-vente/page-meta`, pageMeta);
      toast.success("Textes de la page enregistrés");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSavingMeta(false);
    }
  };

  const copyPassword = () => {
    if (!password) return;
    navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success("Mot de passe copié");
  };

  const savePassword = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.error("Minimum 4 caractères");
      return;
    }
    setSavingPassword(true);
    try {
      await axios.put(`${API}/api/admin/depot-vente/password`, { password: newPassword });
      setPassword(newPassword);
      setNewPassword("");
      toast.success("Mot de passe mis à jour — les sessions actuelles restent valides jusqu'à expiration");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSavingPassword(false);
    }
  };

  const startNew = () => {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, "0");
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const yyyy = today.getFullYear();
    setForm({ ...EMPTY_LISTING_FORM, deposit_date: `${dd}/${mm}/${yyyy}` });
    setEditingId("new");
  };

  const startEdit = (listing) => {
    setForm(listingToForm(listing));
    setEditingId(listing.id);
  };

  const cancelEdit = () => {
    setForm(EMPTY_LISTING_FORM);
    setEditingId(null);
  };

  const saveListing = async () => {
    const title = buildTitle(form);
    if (!title) {
      toast.error("Titre requis (ou marque + modèle)");
      return;
    }
    const km = buildKmLabel(form);
    setSaving(true);
    const payload = { ...form, title, km };
    try {
      if (editingId === "new") {
        await axios.post(`${API}/api/admin/depot-vente/listing`, payload);
        toast.success("Fiche véhicule créée");
      } else {
        await axios.put(`${API}/api/admin/depot-vente/listing/${editingId}`, payload);
        toast.success("Fiche véhicule mise à jour");
      }
      await fetchListings();
      cancelEdit();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const deleteListing = async (listing) => {
    if (!window.confirm(`Supprimer définitivement "${listing.title}" ?`)) return;
    try {
      await axios.delete(`${API}/api/admin/depot-vente/listing/${listing.id}`);
      toast.success("Fiche supprimée");
      await fetchListings();
    } catch {
      toast.error("Erreur à la suppression");
    }
  };

  const setStatus = async (listing, status) => {
    try {
      await axios.put(`${API}/api/admin/depot-vente/listing/${listing.id}`, { status });
      toast.success(`Statut → ${STATUS_META[status]?.label || status}`);
      await fetchListings();
    } catch {
      toast.error("Erreur au changement de statut");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-dv-tab">
      {/* Page copy (public-facing) */}
      <section className="bg-[#121212] border border-white/[0.06] rounded-lg p-5" data-testid="admin-dv-page-meta">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Megaphone size={16} className="text-[#FF3B30]" />
            <h3 className="text-white font-bold">Textes de la page publique</h3>
          </div>
          <button
            onClick={savePageMeta}
            disabled={savingMeta}
            data-testid="admin-dv-page-meta-save"
            className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D63025] disabled:opacity-50 text-white font-bold px-4 py-2 rounded-md text-xs uppercase tracking-wider"
          >
            {savingMeta ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            {savingMeta ? "…" : "Enregistrer"}
          </button>
        </div>
        <p className="text-white/50 text-xs mb-4">
          Ces textes s&apos;affichent sur <code className="text-white/80">/achat-vente-voiture-clermont-ferrand</code> (autour des annonces). Les véhicules eux-mêmes se gèrent plus bas.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">Titre de la section</label>
            <input
              type="text"
              value={pageMeta.section_title}
              onChange={(e) => setPageMeta({ ...pageMeta, section_title: e.target.value })}
              placeholder="Véhicules du moment"
              data-testid="admin-dv-page-title"
              className="w-full bg-[#0A0A0A] border border-white/[0.08] rounded-md px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#FF3B30]"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">Sous-titre</label>
            <input
              type="text"
              value={pageMeta.section_subtitle}
              onChange={(e) => setPageMeta({ ...pageMeta, section_subtitle: e.target.value })}
              placeholder="Sélection actuelle en dépôt-vente — mise à jour régulièrement."
              className="w-full bg-[#0A0A0A] border border-white/[0.08] rounded-md px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#FF3B30]"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">État vide (quand aucun véhicule)</label>
            <input
              type="text"
              value={pageMeta.empty_state}
              onChange={(e) => setPageMeta({ ...pageMeta, empty_state: e.target.value })}
              placeholder="Nouveaux véhicules très bientôt — contactez-nous pour être alerté en premier."
              className="w-full bg-[#0A0A0A] border border-white/[0.08] rounded-md px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#FF3B30]"
            />
          </div>
          <div>
            <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">Libellé du bouton contact</label>
            <input
              type="text"
              value={pageMeta.cta_label}
              onChange={(e) => setPageMeta({ ...pageMeta, cta_label: e.target.value })}
              placeholder="Nous contacter"
              className="w-full bg-[#0A0A0A] border border-white/[0.08] rounded-md px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#FF3B30]"
            />
          </div>
          <div>
            <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">Téléphone (format international)</label>
            <input
              type="text"
              value={pageMeta.cta_phone}
              onChange={(e) => setPageMeta({ ...pageMeta, cta_phone: e.target.value })}
              placeholder="+33649070820"
              className="w-full bg-[#0A0A0A] border border-white/[0.08] rounded-md px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#FF3B30]"
            />
          </div>
        </div>
      </section>

      {/* Password management */}
      <section className="bg-[#121212] border border-white/[0.06] rounded-lg p-5">
        <div className="flex items-center gap-2 mb-3">
          <Key size={16} className="text-[#FF3B30]" />
          <h3 className="text-white font-bold">Mot de passe technicien (Dépôt-Vente)</h3>
        </div>
        <p className="text-white/50 text-xs mb-4">
          Ton technicien utilise ce mot de passe sur <code className="text-white/80">/depot-vente-tech</code> pour ajouter/modifier les véhicules. C&apos;est un mot de passe <strong>séparé</strong> de celui de l&apos;atelier.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">Mot de passe actuel</label>
            <div className="flex gap-2">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                readOnly
                data-testid="admin-dv-password-current"
                className="flex-1 bg-[#0A0A0A] border border-white/[0.08] rounded-md px-3 py-2.5 text-white font-mono"
              />
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="px-3 bg-white/[0.06] hover:bg-white/[0.12] text-white/70 rounded-md text-sm"
                type="button"
              >
                {showPassword ? "Masquer" : "Afficher"}
              </button>
              <button
                onClick={copyPassword}
                data-testid="admin-dv-password-copy"
                className="px-3 bg-white/[0.06] hover:bg-white/[0.12] text-white/70 rounded-md"
                type="button"
              >
                {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-white/60 text-xs font-medium mb-1.5 uppercase tracking-wider">Changer le mot de passe</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="admin-dv-password-new"
                placeholder="Nouveau mot de passe (min. 4)"
                className="flex-1 bg-[#0A0A0A] border border-white/[0.08] rounded-md px-3 py-2.5 text-white"
              />
              <button
                onClick={savePassword}
                disabled={savingPassword || !newPassword}
                data-testid="admin-dv-password-save"
                className="px-4 bg-[#FF3B30] hover:bg-[#D63025] disabled:opacity-50 text-white font-bold rounded-md text-sm uppercase tracking-wider flex items-center gap-1.5"
              >
                {savingPassword ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Listings management */}
      {editingId ? (
        <DepotVenteListingForm
          form={form}
          setForm={setForm}
          saving={saving}
          onCancel={cancelEdit}
          onSave={saveListing}
          isNew={editingId === "new"}
          testIdPrefix="admin-dv-form"
          uploadEndpoint={`/api/admin/depot-vente/upload-image`}
        />
      ) : (
        <section className="bg-[#121212] border border-white/[0.06] rounded-lg p-5">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-white font-bold flex items-center gap-2">
                <Car size={16} className="text-[#FF3B30]" />
                Véhicules en dépôt-vente ({listings.length})
              </h3>
              <p className="text-white/50 text-xs mt-1">Tu vois <strong>toutes les infos</strong> saisies par le technicien : propriétaire, prix vendeur, commission, notes internes... Clique sur une carte pour déplier tous les détails.</p>
            </div>
            <button
              onClick={startNew}
              data-testid="admin-dv-add-btn"
              className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#D63025] text-white font-bold px-4 py-2.5 rounded-md text-sm uppercase tracking-wider"
            >
              <Plus size={14} />
              Ajouter
            </button>
          </div>

          {loading ? (
            <div className="text-center py-10 text-white/40">
              <Loader2 size={20} className="animate-spin mx-auto mb-2" />
              Chargement...
            </div>
          ) : listings.length === 0 ? (
            <div className="text-center py-10 border border-dashed border-white/[0.06] rounded-lg">
              <Car size={32} className="text-white/20 mx-auto mb-2" />
              <p className="text-white/60 text-sm">Aucun véhicule en dépôt-vente pour le moment</p>
            </div>
          ) : (
            <div className="space-y-3">
              {listings.map((v) => (
                <AdminListingCard
                  key={v.id}
                  v={v}
                  expanded={expandedId === v.id}
                  onToggleExpand={() => setExpandedId(expandedId === v.id ? null : v.id)}
                  onEdit={() => startEdit(v)}
                  onDelete={() => deleteListing(v)}
                  onStatusChange={(s) => setStatus(v, s)}
                />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

// ============== ADMIN LISTING CARD ==============
const AdminListingCard = ({ v, expanded, onToggleExpand, onEdit, onDelete, onStatusChange }) => {
  const cover = v.image_url || (Array.isArray(v.images) && v.images[0]) || "";
  const photosCount = (v.images?.length || 0) + (v.image_url ? 1 : 0);
  return (
    <article
      data-testid={`admin-dv-listing-${v.id}`}
      className="bg-[#0A0A0A] border border-white/[0.06] rounded-md overflow-hidden"
    >
      <div className="flex flex-col md:flex-row">
        <button
          type="button"
          onClick={onToggleExpand}
          className="md:w-40 h-32 md:h-auto bg-[#1a1a1a] flex-shrink-0 relative overflow-hidden"
          title="Cliquer pour déplier les détails"
        >
          {cover ? (
            <img src={buildImageUrl(cover)} alt={v.title} loading="lazy" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center"><Car size={30} className="text-white/10" /></div>
          )}
          {photosCount > 1 && (
            <div className="absolute bottom-1 right-1 bg-black/70 text-white text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1">
              <Camera size={9} /> {photosCount}
            </div>
          )}
        </button>

        <div className="p-4 flex-1 flex flex-col">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="min-w-0 flex-1 cursor-pointer" onClick={onToggleExpand}>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h4 className="text-white font-bold truncate">{v.title || "(sans titre)"}</h4>
                <StatusPill status={v.status} />
              </div>
              {v.price && <div className="text-[#FF3B30] font-bold">{v.price}</div>}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <select
                value={v.status || "disponible"}
                onChange={(e) => onStatusChange(e.target.value)}
                data-testid={`admin-dv-status-${v.id}`}
                className="bg-[#121212] border border-white/[0.06] rounded-md px-2 py-1 text-white/80 text-xs"
              >
                <option value="disponible">Disponible</option>
                <option value="reserve">Réservé</option>
                <option value="vendu">Vendu</option>
              </select>
              <a
                href={`/depot-vente/fiche/${v.id}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`admin-dv-pdf-${v.id}`}
                onClick={(e) => e.stopPropagation()}
                className="text-white/60 hover:text-[#FF3B30] p-2 rounded-md hover:bg-white/[0.06]"
                title="Fiche PDF imprimable"
              >
                <FileText size={14} />
              </a>
              <button
                onClick={onEdit}
                data-testid={`admin-dv-edit-${v.id}`}
                className="text-white/60 hover:text-white p-2 rounded-md hover:bg-white/[0.06]"
                title="Modifier"
              >
                <Edit size={14} />
              </button>
              <button
                onClick={onDelete}
                data-testid={`admin-dv-delete-${v.id}`}
                className="text-white/60 hover:text-red-400 p-2 rounded-md hover:bg-red-500/[0.06]"
                title="Supprimer"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1 text-xs text-white/60 mb-2">
            {v.year && <div className="flex items-center gap-1.5"><Calendar size={11} className="text-[#FF3B30]" /> {v.year}</div>}
            {v.km && <div className="flex items-center gap-1.5"><Gauge size={11} className="text-[#FF3B30]" /> {v.km}</div>}
            {v.fuel && <div className="flex items-center gap-1.5"><Fuel size={11} className="text-[#FF3B30]" /> {v.fuel}</div>}
            {v.gearbox && <div className="flex items-center gap-1.5"><Cog size={11} className="text-[#FF3B30]" /> {v.gearbox}</div>}
          </div>

          {v.vin && <div className="text-white/40 text-[11px] font-mono mb-2">VIN {v.vin}</div>}

          <div className="flex items-center gap-3 text-xs text-white/40 mt-auto pt-2 border-t border-white/[0.04] flex-wrap">
            {v.created_by && (
              <span className="inline-flex items-center gap-1"><User size={10} /> {v.created_by}</span>
            )}
            {v.created_at && (
              <span className="inline-flex items-center gap-1"><Clock size={10} /> {formatDate(v.created_at)}</span>
            )}
            {v.updated_at && v.updated_at !== v.created_at && (
              <span className="inline-flex items-center gap-1 text-white/30">
                maj {formatDate(v.updated_at)}
              </span>
            )}
            <button
              onClick={onToggleExpand}
              data-testid={`admin-dv-expand-${v.id}`}
              className="ml-auto text-[#FF3B30] hover:text-[#ff6b62] font-semibold uppercase tracking-wider"
            >
              {expanded ? "Réduire ▲" : "Voir tous les détails ▼"}
            </button>
          </div>
        </div>
      </div>

      {expanded && <ExpandedDetails v={v} />}
    </article>
  );
};

const ExpandedDetails = ({ v }) => (
  <div className="border-t border-white/[0.06] bg-[#121212] p-4 space-y-4 text-sm">
    {/* Identity */}
    <DetailBlock title="Identification">
      <DetailRow label="Marque" value={v.brand} />
      <DetailRow label="Modèle" value={v.model} />
      <DetailRow label="Finition" value={v.trim} />
      <DetailRow label="1ère immat" value={v.first_registration} />
      <DetailRow label="Immatriculation" value={v.plate} />
      <DetailRow label="VIN" value={v.vin} mono />
    </DetailBlock>

    {/* Technical */}
    <DetailBlock title="Caractéristiques techniques">
      <DetailRow label="Motorisation" value={v.engine} />
      <DetailRow label="Puissance DIN" value={v.power_din ? `${v.power_din} ch` : ""} />
      <DetailRow label="CV fiscaux" value={v.fiscal_hp} />
      <DetailRow label="Cylindrée" value={v.displacement ? `${v.displacement} cm³` : ""} />
      <DetailRow label="CO₂" value={v.co2 ? `${v.co2} g/km` : ""} />
      <DetailRow label="Crit'Air" value={v.crit_air} />
      <DetailRow label="Portes" value={v.doors} />
      <DetailRow label="Places" value={v.seats} />
      <DetailRow label="Couleur ext." value={v.color_exterior} />
      <DetailRow label="Couleur int." value={v.color_interior} />
    </DetailBlock>

    {/* Condition */}
    <DetailBlock title="Kilométrage & État">
      <DetailRow label="Km exact" value={v.exact_km} />
      <DetailRow label="Propriétaires précédents" value={v.previous_owners} />
      <DetailRow label="CT valide jusqu'au" value={v.ct_valid_until} />
      <DetailRow label="Résultat CT" value={v.ct_result} />
      <DetailRow label="Nombre de clés" value={v.keys_count} />
      <DetailRow label="Carnet entretien" value={v.service_book} />
      <DetailRow label="Dernier entretien (date)" value={v.last_service_date} />
      <DetailRow label="Dernier entretien (km)" value={v.last_service_km} />
      <DetailRow label="Accident / sinistre" value={v.accident_history} />
      <DetailRow label="État des pneus" value={v.tires_condition} />
      {v.condition_notes && <DetailRow label="Défauts / notes" value={v.condition_notes} multiline />}
    </DetailBlock>

    {/* Options */}
    {v.options && v.options.length > 0 && (
      <DetailBlock title={`Équipements (${v.options.length})`}>
        <div className="flex flex-wrap gap-1.5 col-span-full">
          {v.options.map((o, i) => (
            <span key={i} className="text-xs text-white/80 bg-white/[0.06] border border-white/[0.08] px-2 py-0.5 rounded-full">{o}</span>
          ))}
        </div>
      </DetailBlock>
    )}

    {/* Description */}
    {v.description && (
      <DetailBlock title="Description publique">
        <div className="col-span-full text-white/80 text-sm whitespace-pre-wrap">{v.description}</div>
      </DetailBlock>
    )}

    {/* Photos */}
    {(v.images && v.images.length > 0) && (
      <DetailBlock title={`Photos additionnelles (${v.images.length})`}>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 col-span-full">
          {v.images.map((url, i) => (
            <div key={i} className="aspect-video rounded-md overflow-hidden border border-white/[0.06] bg-[#1a1a1a]">
              <img src={buildImageUrl(url)} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
            </div>
          ))}
        </div>
      </DetailBlock>
    )}

    {/* Commercial (private) */}
    <DetailBlock title="Commercial (privé)">
      <DetailRow label="Date de dépôt" value={v.deposit_date} />
      <DetailRow label="Prix vendeur souhaité" value={v.seller_price} />
      <DetailRow label="Prix affiché (public)" value={v.price} />
      <DetailRow label="Commission (€)" value={v.commission_amount} />
      <DetailRow label="Commission (%)" value={v.commission_percent ? `${v.commission_percent} %` : ""} />
    </DetailBlock>

    {/* Owner (private) */}
    <DetailBlock title="Propriétaire (privé)">
      <DetailRow label="Nom complet" value={[v.owner_first_name, v.owner_last_name].filter(Boolean).join(" ")} />
      <DetailRow label="Téléphone" value={v.owner_phone} icon={v.owner_phone && <Phone size={11} />} />
      <DetailRow label="Email" value={v.owner_email} icon={v.owner_email && <Mail size={11} />} />
      <DetailRow label="Adresse" value={v.owner_address} multiline />
    </DetailBlock>

    {/* Internal notes */}
    {v.technical_notes && (
      <DetailBlock title="Notes techniques internes">
        <div className="col-span-full text-white/80 text-sm whitespace-pre-wrap bg-[#0A0A0A] border border-white/[0.06] rounded p-3">{v.technical_notes}</div>
      </DetailBlock>
    )}
  </div>
);

const DetailBlock = ({ title, children }) => {
  // Filter out null/false children so empty blocks aren't rendered
  const visibleChildren = React.Children.toArray(children).filter(
    (c) => c !== null && c !== undefined && c !== false && c !== ""
  );
  if (visibleChildren.length === 0) return null;
  return (
    <div>
      <h5 className="text-white/50 text-[10px] font-bold uppercase tracking-widest mb-2">{title}</h5>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1.5">{visibleChildren}</div>
    </div>
  );
};

const DetailRow = ({ label, value, mono = false, multiline = false, icon = null }) => {
  if (!value) return null;
  return (
    <div className={multiline ? "col-span-full" : ""}>
      <div className="text-white/40 text-[10px] uppercase tracking-wider">{label}</div>
      <div className={`text-white ${mono ? "font-mono text-xs" : "text-sm"} flex items-center gap-1.5 ${multiline ? "whitespace-pre-wrap" : "truncate"}`}>
        {icon}
        {value}
      </div>
    </div>
  );
};
